import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faPen,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { selectUser } from "../../redux/slices/userSlice";
import { getUserRoles, hasRole } from "../../utils/roles";

const API = "https://api.sedihisham.com";

const emptyQuestion = {
  id: null,
  question_ar: "",
  question_en: "",
  required: true,
  active: true,
  sort_order: 0,
};

const inputClass =
  "w-full h-[34px] rounded border-[#c4c8cf] text-[14px] font-[400] placeholder:text-[12px]";

// General questions asked in every job application on the website (on top of
// the questions of the chosen job).
function ApplicationQuestions() {
  const currentUser = useSelector(selectUser);
  const disabled = !hasRole(getUserRoles(currentUser), ["HR", "Admin"]);
  const [questions, setQuestions] = useState(null);
  const [values, setValues] = useState(emptyQuestion);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/careers/questions?all=1`)
      .then((res) => setQuestions(Array.isArray(res.data) ? res.data : []))
      .catch(() => {
        setQuestions([]);
        setError("Could not load the questions — the server may need the latest update.");
      });
  }, []);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setValues({ ...values, [name]: type === "checkbox" ? checked : value });
  };

  const send = async (question, id) => {
    const url = id ? `${API}/careers/questions/${id}` : `${API}/careers/questions`;
    const res = await axios.post(url, question);
    setQuestions(Array.isArray(res.data) ? res.data : questions);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!values.question_ar.trim()) {
      setError("Please write the question in Arabic");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await send(values, values.id);
      setValues(emptyQuestion);
      Swal.fire({
        icon: "success",
        title: "Your changes had been saved",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Could not save the question.");
    }
    setSaving(false);
  };

  const toggle = async (question, field) => {
    try {
      await send({ ...question, [field]: !question[field] }, question.id);
    } catch (err) {
      setError("Could not update the question.");
    }
  };

  const remove = async (question) => {
    const answer = await Swal.fire({
      icon: "warning",
      title: "Delete this question?",
      text: question.question_ar,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!answer.isConfirmed) return;
    try {
      const res = await axios.get(`${API}/careers/questions/delete/${question.id}`);
      setQuestions(res.data);
    } catch (err) {
      setError("Could not delete the question.");
    }
  };

  return (
    <div className="w-[95%] pb-10">
      <form onSubmit={save} className="rounded-xl border border-[#E9F6FD] bg-[#fafdff] p-4">
        <p className="text-[15px] font-[700] text-[#033362]">
          {values.id ? "Edit question" : "Add new question"}
        </p>
        <p className="text-[12px] font-[400] text-[#707070] mb-3">
          These questions appear in every job application on the website, in
          the last step, together with the questions of the chosen job.
        </p>
        <div className="grid grid-cols-2 gap-3 text-[14px] font-[400]">
          <label>
            Question - AR <span className="text-[red]">*</span>
            <input dir="rtl" name="question_ar" value={values.question_ar} onChange={change} className={inputClass} placeholder="مثال: كم سنة خبرة لديك؟" />
          </label>
          <label>
            Question - EN
            <input name="question_en" value={values.question_en || ""} onChange={change} className={inputClass} placeholder="e.g. How many years of experience do you have?" />
          </label>
          <label>
            Order
            <input type="number" name="sort_order" value={values.sort_order} onChange={change} className={inputClass} />
          </label>
          <div className="flex items-end gap-6 pb-1">
            <label className="flex items-center gap-2">
              <input type="checkbox" name="required" checked={values.required} onChange={change} className="rounded" />
              Required answer
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" name="active" checked={values.active} onChange={change} className="rounded" />
              Active (asked on the website)
            </label>
          </div>
        </div>
        {error ? (
          <p className="text-[14px] font-[400] mt-3 text-[red]">
            <FontAwesomeIcon icon={faCircleExclamation} /> {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 mt-3">
          {values.id && (
            <div onClick={() => setValues(emptyQuestion)} className="cursor-pointer rounded-full text-[13px] px-6 py-1 text-[#0388CC] bg-[#E9F6FD]">
              Cancel
            </div>
          )}
          <input type="submit" disabled={disabled || saving} value={values.id ? "Save" : "Add question"} className="cursor-pointer rounded-full text-[13px] px-6 py-1 text-[#fff] bg-[#0388CC] disabled:opacity-50" />
        </div>
      </form>

      <table className="mt-6 w-full text-[14px] font-[400]">
        <thead>
          <tr className="text-left text-[#033362]">
            <th className="py-2">#</th>
            <th>Question</th>
            <th>Required</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {questions === null ? (
            <tr><td className="py-3" colSpan={5}>Loading...</td></tr>
          ) : questions.length === 0 ? (
            <tr><td className="py-3" colSpan={5}>No general questions yet.</td></tr>
          ) : (
            questions.map((question, i) => (
              <tr key={question.id}>
                <td className="py-2">{i + 1}</td>
                <td>
                  <div dir="rtl" className="text-left text-[#0388CC]">{question.question_ar}</div>
                  <div className="text-[12px] text-[#707070]">{question.question_en}</div>
                </td>
                <td>
                  <span onClick={() => !disabled && toggle(question, "required")} className={`cursor-pointer rounded px-3 py-[2px] text-[12px] text-[white] ${question.required ? "bg-[#cc4125]" : "bg-[#9a9a9a]"}`}>
                    {question.required ? "Required" : "Optional"}
                  </span>
                </td>
                <td>
                  <span onClick={() => !disabled && toggle(question, "active")} className={`cursor-pointer rounded px-3 py-[2px] text-[12px] text-[white] ${question.active ? "bg-[#007530]" : "bg-[#9a9a9a]"}`}>
                    {question.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="text-right whitespace-nowrap">
                  <span onClick={() => !disabled && setValues({ ...emptyQuestion, ...question })} className="cursor-pointer rounded px-3 py-[2px] mr-2 text-[white] bg-[#0388CC]">
                    <FontAwesomeIcon icon={faPen} />
                  </span>
                  <span onClick={() => !disabled && remove(question)} className="cursor-pointer rounded px-3 py-[2px] text-[white] bg-[red]">
                    <FontAwesomeIcon icon={faTrash} />
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default ApplicationQuestions;
